// @react
import React, { useState, useCallback, ReactElement, useEffect } from 'react'
// @common
import config from 'constants/config'
import { doLogout } from 'modules/auth/helpers'
import useDebounce from 'modules/common/hooks/useDebounce'
import algoliasearch from 'algoliasearch'
import { matchPath, useLocation } from 'react-router-dom'
// @components
import { Waypoint } from 'react-waypoint'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { MenuItem } from '@bit/atd.web.header/dist/types'
import icons from '@bit/atd.web.icons/dist/getIcons'
import { SearchResponse, Hit } from '@algolia/client-search'
import { PropsType as IconProps } from '@bit/atd.web.icons'
// @graphql
import { useQuery } from '@apollo/client'
import { GET_USER } from 'graphql/queries/user.query'
import Header, { SimpleUserData } from '__COMPONENTS_TO_MIGRATE/Header'
import {
  GetFeaturedContentQuery,
  GetFeaturedContentQueryVariables,
  GetMenuQuery,
  GetMenuQueryVariables,
  GetUserQuery,
  GetUserQueryVariables,
  UserSearchItem,
} from 'lib/graphql/graphqlTypes'
import Helper from 'utils/Helper.class'
import { GET_MENU } from 'graphql/queries/menu.query'
import { PropsType as CardProps } from '__COMPONENTS_TO_MIGRATE/CardNew'
import TextLink from '@bit/atd.web.text-link'
import { GET_FEATURED_CONTENT } from 'graphql/queries/featuredContent.query'
import TopBanner from '../../../../__COMPONENTS_TO_MIGRATE/TopBanner'

const client = algoliasearch(
  process.env.REACT_APP_ALGOLIA_APP_ID as string,
  process.env.REACT_APP_ALGOLIA_SECRET as string,
)

type PropsType = {
  data: MenuItem[]
  isShowSearch: boolean
  setIsShowSearch: (isShowSearch: boolean) => void
  setIsAuth: (value: boolean) => void
}

type MappedCourses = {
  results?: number
  hits?: Hit<any>[]
}

type CategorizedResult = {
  category: string
  viewHref: string
  results?: number
  content: {
    title: string
    imageUrl: string
    objectID: string
    url: string
  }[]
}

const searchHref = Helper.getPathFromRouteKey('search')

/**
 *
 * @param category
 */
const getIconForCategory = (category: string) =>
  icons[category as IconProps['glyph']] ? category : 'articles'

/**
 *
 * @param authorsContent
 * @param publishDate
 */
const authorFormatter = (authorsContent: any, publishDate: any) => {
  const byLine: CardProps['content']['byLine'] = {}

  const authors = authorsContent?.map((author: any) => {
    const auth = author as UserSearchItem

    const fullName = auth?.fullName
    const firstName = auth?.firstName
    const lastName = auth?.lastName

    return {
      author:
        fullName ||
        (firstName && lastName && `${firstName} ${lastName}`) ||
        (firstName && firstName) ||
        (lastName && lastName) ||
        '',
    }
  })
  const pubDate = Helper.smallDateFormatter(publishDate)

  authors && (byLine.authors = authors)
  pubDate && (byLine.pubDate = pubDate)

  return byline(byLine)
}

/**
 *
 * @param byLine
 */
const byline = (byLine: any) => {
  return (
    <p>
      {!!byLine?.authors && !!byLine?.authors.length && (
        <>
          <span>By </span>
          {byLine?.authors?.map(
            ({ author, url }: { author: any; url: any }, index: number) => (
              <React.Fragment key={`${index}_${author}`}>
                {url && (
                  <TextLink variant={'secondary'} href={url}>
                    {author}
                  </TextLink>
                )}
                {!url && <span>{author}</span>}
                {index < byLine?.authors!.length - 2 && ', '}
                {index === byLine?.authors!.length - 2 && ' & '}
              </React.Fragment>
            ),
          )}
        </>
      )}
      {byLine?.authors && byLine?.pubDate && ' '}
      {byLine?.pubDate &&
        `${!!byLine?.authors?.length ? 'on' : 'Published on'} ${
          byLine?.pubDate
        }`}
    </p>
  )
}

const mapContent = ({ hits, nbHits }: SearchResponse<unknown>) => {
  return {
    hits: hits.map(
      ({
        url,
        contentTypesArray,
        publishDate,
        authors = [],
        _snippetResult: { title, description },
      }: Hit<any>) => {
        const category = contentTypesArray
          ? contentTypesArray[contentTypesArray.length - 1]
          : ''
        return {
          url,
          title: title?.value || '',
          categoryText: category,
          categoryIcon: getIconForCategory(category),
          description: description?.value || '',
          createdInfo: authorFormatter(authors, publishDate),
        }
      },
    ),
    results: nbHits,
  }
}

const defaultTopOffset = -125
const HeaderWrap = ({
  data,
  isShowSearch,
  setIsShowSearch,
  setIsAuth,
}: PropsType): ReactElement => {
  const location = useLocation()

  const isOnboarding = !!matchPath(location.pathname, {
    path: Helper.getPathFromRouteKey('ONBOARDING'),
    exact: false,
    strict: false,
  })
  /******************************
   * STATE
   *******************************/
  const [sticky, setSticky] = useState(false)
  const [searchKey, setSearchKey] = useState('')
  const searchText = useDebounce(searchKey)
  const [courses, setCourses] = useState<CategorizedResult>()
  const [publications, setPublications] = useState<CategorizedResult>()
  const [conferences, setConferences] = useState<CategorizedResult>()
  const [suggestedHints, setSuggestedHints] = useState<string[]>([])
  const [content, setContent] = useState<MappedCourses>({})
  const [featuredToday, setFeaturedToday] = useState<any[]>([])
  const [topOffset, setTopOffset] = useState(defaultTopOffset)

  /********************************************
   *  HOOKS
   *********************************************/
  const theme = useTheme()

  /********************************************
   *  GQL HOOKS
   *********************************************/
  const { data: userData, loading } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER)

  const { data: menuData } = useQuery<GetMenuQuery, GetMenuQueryVariables>(
    GET_MENU,
    {
      skip: isOnboarding,
    },
  )

  const { data: featuredData } = useQuery<
    GetFeaturedContentQuery,
    GetFeaturedContentQueryVariables
  >(GET_FEATURED_CONTENT, {
    variables: {
      input: {
        page: 0,
        pageSize: 10,
      },
    },
    skip: !userData?.user,
  })

  /**
   *
   */
  const collapseHeaderMQ = useMediaQuery(
    // @ts-ignore
    theme.breakpoints.between('switchToMobileHeader', 'sm'),
    config.mediaQuery,
  )

  /********************************************
   *  FUNCTIONS
   *********************************************/

  /**
   *
   * @param resp
   * @param index
   */
  const mapSearchResponse = (resp: SearchResponse<unknown>, index: string) => {
    const { hits, nbHits } = resp
    return {
      category: index,
      content: hits
        .slice(0, 2)
        .map(({ _highlightResult, imageUrl, url, objectID }: Hit<any>) => ({
          title: _highlightResult.title.value,
          imageUrl,
          url,
          objectID,
        })),
      viewHref: `${searchHref}/${encodeURIComponent(
        index,
      )}?query=${encodeURIComponent(searchText)}`,
      results: nbHits,
    }
  }

  useEffect(() => {
    if (isShowSearch) {
      const courseIndex = client.initIndex('courses')
      courseIndex
        .search(searchText)
        .then((res) => setCourses(mapSearchResponse(res, 'courses')))

      const publicationsIndex = client.initIndex('publications')
      publicationsIndex
        .search(searchText)
        .then((res) => setPublications(mapSearchResponse(res, 'publications')))

      const conferencesIndex = client.initIndex('conferences')
      conferencesIndex
        .search(searchText)
        .then((res) => setConferences(mapSearchResponse(res, 'conferences')))

      const suggestionsIndex = client.initIndex('master_query_suggestions')
      suggestionsIndex
        .search(searchText)
        .then(({ hits }) =>
          setSuggestedHints(hits.slice(-5).map(({ query }: Hit<any>) => query)),
        )

      const contentIndex = client.initIndex('content')
      contentIndex
        .search(searchText, { hitsPerPage: 5 })
        .then((res) => setContent(mapContent(res)))

      const featuredTodayIndex = client.initIndex('master')
      featuredTodayIndex
        .search(searchText)
        .then(() => setFeaturedToday(featuredData?.featuredContent?.data || []))
    }
  }, [searchText, isShowSearch])

  const onWaypointPositionChange = useCallback((current: any) => {
    const isAbove = current.currentPosition === 'above'
    setSticky(isAbove)
    setTopOffset(isAbove ? 0 : defaultTopOffset)
  }, [])

  const searchHistory = localStorage.getItem('searchHistory')
  const recentHistories = (searchHistory
    ? JSON.parse(searchHistory)
    : []
  ).slice(-10)

  const categorizedResults = [courses, publications, conferences].reduce(
    (results: CategorizedResult[], item) =>
      item ? [...results, item] : results,
    [],
  )

  const mainSearchHref = location.pathname.startsWith(searchHref)
    ? location.pathname
    : searchHref

  const userInfo: SimpleUserData = userData?.user
    ? Object.assign(
        {
          membershipType: undefined,
          avatarUrl: undefined,
        },
        {
          firstName: userData.user.profile.firstName,
          lastName: userData.user.profile.lastName,
          membershipType: userData.user.membership.overview?.label,
          avatarUrl: userData.user.profile.profileImageUrl,
          bgColor: Helper.randomColorChooser(),
          textAs: Helper.getAcronymFromSentence(
            `${userData.user.profile.firstName} ${userData.user.profile.lastName}`,
          ),
          cartCount: userData.user.cart.itemTotal,
        },
      )
    : null

  /********************************************
   *  RENDER
   *********************************************/
  if (isOnboarding) return <></>
  return (
    <>
      <Waypoint
        onPositionChange={onWaypointPositionChange}
        topOffset={topOffset}
      />
      {menuData?.menu.bannerData && (
        <TopBanner data={menuData?.menu.bannerData} />
      )}
      <Header
        // @ts-ignore
        data={menuData?.menu.menuData || data}
        condensed={sticky || collapseHeaderMQ}
        doLogout={doLogout}
        onLogin={() => setIsAuth(true)}
        isLoggedIn={!!userData?.user}
        user={userInfo}
        isUserLoading={loading}
        isNewSearch={location.pathname.startsWith('/search')}
        searchPanelProps={{
          searchKey,
          suggestedHints,
          recentHistories,
          featuredToday,
          categorizedResults,
          integratedResults: content?.hits || [],
          mainSearchHref: `${mainSearchHref}?query=${encodeURIComponent(
            searchKey,
          )}`,
          totalResults: [courses, publications, conferences, content].reduce(
            (total: number, item) => total + (item?.results || 0),
            0,
          ),
          showOverlay: true,
          onChange: (value: string) => setSearchKey(value),
          isShowSearch,
          setIsShowSearch,
        }}
      />
    </>
  )
}

export default HeaderWrap
